import React, { useMemo } from 'react'
import { t } from 'i18next'
import {
  formatCustomerDisplayDate,
  getFileName,
  isVideo,
} from '../utils/customerDisplay.utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlayCircle,
  faEye,
  faBars,
  faSquarePen,
} from '@fortawesome/free-solid-svg-icons'
import ToolTipInformation from 'Components/ToolTipInformation/ToolTipInformation'

function useColumnSearchCustomerDisplay({ onPreview, onEdit, onToggleStatus }) {
  return useMemo(
    () => [
      {
        Header: '',
        accessor: 'drag',
        width: 40,
        Cell: () => (
          <FontAwesomeIcon icon={faBars} style={{ fontSize: '2rem' }} />
        ),
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.PREVIEW'),
        accessor: 'preview',
        width: 90,
        Cell: ({ row }) => {
          const item = row.original

          return isVideo(item.MediaType) ? (
            <div className="container-btn-customer-display-preview">
              <button
                className="btn-customer-display-preview"
                onClick={() => onPreview(item)}
                data-testid={`btn-preview-${item.CustomerDisplaySettingId}`}>
                <FontAwesomeIcon
                  icon={faPlayCircle}
                  style={{ color: '#1b75d2', fontSize: '2rem' }}
                />
              </button>
            </div>
          ) : (
            <img
              src={item.FileUrl}
              alt={item.Name || ''}
              width={60}
              height={40}
              className="customer-display-thumb"
              onClick={() => onPreview(item)}
            />
          )
        },
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.MEDIA_TYPE'),
        accessor: 'MediaType',
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.NAME'),
        accessor: 'Name',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.FILE_NAME'),
        accessor: 'FileUrl',
        Cell: ({ value }) => getFileName(value),
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.COMPANY'),
        accessor: 'CompanyName',
        Cell: ({ value, row }) => {
          const branchOfficeNames = (row.original.BranchOffices || [])
            .map(branchOffice => branchOffice.BranchOfficeName)
            .filter(Boolean)

          return (
            <div className="customer-display-company-cell">
              <span>{value || '—'}</span>
              {branchOfficeNames.length > 0 && (
                <ToolTipInformation
                  message={
                    <p style={{ fontSize: '12px' }}>
                      {branchOfficeNames.map((branchOfficeName, index) => (
                        <React.Fragment key={branchOfficeName}>
                          {index > 0 && <br />}
                          {branchOfficeName}
                        </React.Fragment>
                      ))}
                    </p>
                  }
                />
              )}
            </div>
          )
        },
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.START_DATE'),
        accessor: 'StartDate',
        Cell: ({ value }) => formatCustomerDisplayDate(value),
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.END_DATE'),
        accessor: 'EndDate',
        Cell: ({ value }) => formatCustomerDisplayDate(value),
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.STATUS'),
        accessor: 'Status',
        Cell: ({ row }) => (
          <div className="toggle">
            <label className="switch">
              <input
                type="checkbox"
                checked={!!row.original.Status}
                name="switch-media-status"
                value={row.original.Status}
                onChange={() => onToggleStatus(row.original)}
                data-testid={`input-media-status-${row.original.CustomerDisplaySettingId}`}
              />
              <span className="slider round"></span>
            </label>
          </div>
        ),
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.DISPLAY_ORDER'),
        accessor: 'DisplayOrder',
        Cell: ({ value }) => value ?? '—',
      },
      {
        Header: t('CUSTOMER_DISPLAY_SETTINGS.LIST.ACTIONS'),
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className="customer-display-actions">
            <button
              onClick={() => onEdit(row.original)}
              data-testid={`btn-edit-${row.original.CustomerDisplaySettingId}`}>
              <FontAwesomeIcon
                icon={faSquarePen}
                style={{
                  color: '#3FC5DF',
                  fontSize: '2rem',
                }}
              />
            </button>
            <button
              onClick={() => onPreview(row.original)}
              data-testid={`btn-preview-${row.original.CustomerDisplaySettingId}`}>
              <FontAwesomeIcon
                icon={faEye}
                style={{
                  color: '#11D29E',
                  fontSize: '2rem',
                }}
              />
            </button>
          </div>
        ),
      },
    ],
    [onPreview, onEdit, onToggleStatus]
  )
}

export default useColumnSearchCustomerDisplay
